<template>
  <Section
    :id="id"
    :header="t('consolidation.openOrders')"
    class="open-ship-orders-section"
  >
    <ShipOrderSearchBuilder
      :id="`${id}-search-builder`"
      :model-value="modelValue"
      @update:model-value="emit('update:model-value', $event)"
    />

    <ShipOrderLoadingCard
      v-if="searchResults.loading || consolidationCandidates.loading"
    />
    <p v-else-if="!consolidationCandidates.data?.length">
      {{
        t('common.noParamMatchingSearch', {
          param: t('consolidation.candidates', 2),
        })
      }}
    </p>
    <template v-else>
      <ul role="list" class="open-ship-orders-list">
        <li
          v-for="shipOrder in consolidationCandidates.data"
          :key="shipOrder.id"
          class="open-ship-order"
        >
          <OpenShipOrderCard
            :ship-order="shipOrder"
            addable
            draggable
            @dragstart="onDragStart"
            @dragend="onDragEnd"
            @add-ship-order="emit('add-ship-order', $event)"
            @refresh-candidates="emit('refresh-candidates')"
          />
        </li>
      </ul>
    </template>

    <Paginator
      class="paginator elevated"
      :first="searchResults.data?.pageable?.offset ?? 0"
      :rows="modelValue.pageSize"
      :total-records="searchResults.data?.totalElements ?? 0"
      :rows-per-page-options="[10, 20, 50]"
      current-page-report-template="Showing {first} to {last} of {totalRecords}"
      template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      @page="emit('page', $event)"
      @sort="emit('sort', $event)"
    />
  </Section>
</template>

<script lang="ts" setup>
import { PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { Loadable } from '@rygen/rygen-ui/model';

import { ShipOrder, ShipOrderSearchResult } from '@/model';
import { ShipOrderSearchCriteria } from '@/model/consolidation';

import OpenShipOrderCard from '@/components/shipmentPlanning/consolidation/OpenShipOrderCard.vue';
import ShipOrderSearchBuilder from '@/components/shipmentPlanning/consolidation/search/ShipOrderSearchBuilder.vue';
import ShipOrderLoadingCard from '@/components/shipmentPlanning/consolidation/ShipOrderLoadingCard.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import Paginator, { PageState } from 'primevue/paginator';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: ShipOrderSearchCriteria,
    default: () => new ShipOrderSearchCriteria(),
  },
  consolidationCandidates: {
    type: Object as PropType<Loadable<ShipOrder[]>>,
    default: () => ({ loading: false, data: [] }),
  },
  searchResults: {
    type: Object as PropType<Loadable<ShipOrderSearchResult>>,
    default: () => ({ loading: false }),
  },
});
const { id, modelValue, consolidationCandidates, searchResults } =
  toRefs(props);

const emit = defineEmits<{
  (event: 'add-ship-order', shipOrder: ShipOrder): void;
  (event: 'page', pageState: PageState): void;
  (event: 'sort', payload: any): void;
  (event: 'refresh-candidates'): void;
  (event: 'update:model-value', searchCriteria: ShipOrderSearchCriteria): void;
}>();

const { t } = useI18n();

const onDragStart = (event: DragEvent) => {
  if (!event.dataTransfer) {
    console.warn('No dataTransfer object for drag. WTF');
    return;
  }

  event.dataTransfer.dropEffect = 'move';
  event.dataTransfer.effectAllowed = 'move';
  if (event.target) {
    const target = event.target as HTMLElement;
    target.classList.add('dragging');
    if (target.dataset.shipOrderId) {
      event.dataTransfer.setData('text/plain', target.dataset.shipOrderId);
    }
  }
};

const onDragEnd = (event: DragEvent) => {
  if (!event.dataTransfer) {
    console.warn('No dataTransfer object for drag. WTF');
    return;
  }

  if (event.target) {
    const target = event.target as HTMLElement;
    target.classList.remove('dragging');
  }
};
</script>

<style>
.open-ship-orders-section {
  height: 100%;
  padding: var(--sp-6) 0 0;

  .section-data {
    .open-ship-orders-list {
      list-style: none;
      margin: var(--sp-6) 0 0;
      padding: 0;

      /* 100% - filters height - paginator height - body padding */
      height: calc(100% - 155.5px - var(--sp-6) - 93px - var(--sp-6));
      overflow: hidden auto;

      .open-ship-order {
        & + .open-ship-order {
          margin-top: var(--sp-3);
        }

        .open-ship-order-card.p-card {
          &.dragging {
            border-color: var(--p-blue-500);
            cursor: grabbing;
            opacity: 0.5;
          }
        }
      }
    }
  }

  .paginator {
    margin-top: var(--sp-6);
  }
}
</style>
